import React, { useState, useEffect } from 'react';
import { Shield, Mail, FileText, LogOut, Inbox } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminBlogCampaign from '@/components/AdminBlogCampaign';
import CTIBriefingManager from '@/components/CTIBriefingManager';
import AdminContactSubmissions from '@/components/AdminContactSubmissions';
import AdminLogin from '@/components/AdminLogin';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);

  // Determine active tab from URL
  const getActiveTabFromPath = () => {
    if (location.pathname === '/admin/submissions') return 'submissions';
    if (location.pathname === '/admin/campaigns') return 'campaigns';
    if (location.pathname === '/admin/cti') return 'cti';
    return 'submissions'; // default
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Update active tab when location changes
  useEffect(() => {
    const newActiveTab = getActiveTabFromPath();
    setActiveTab(newActiveTab);
    
    // Redirect to submissions if on base /admin path
    if (location.pathname === '/admin') {
      navigate('/admin/submissions', { replace: true });
    }
  }, [location.pathname, navigate]);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/admin/verify', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setUser(data.user);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
    }
  };

  const handleLogin = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-[#00a86b] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const tabs = [
    { id: 'submissions', label: 'Contact Submissions', icon: Inbox, path: '/admin/submissions' },
    { id: 'campaigns', label: 'Blog Campaigns', icon: Mail, path: '/admin/campaigns' },
    { id: 'cti', label: 'CTI Briefings', icon: FileText, path: '/admin/cti' }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Enterprise Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Brand Section */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg">
                  <img
                    src="/images/cipher_character_notext.png"
                    alt="CIPHER Security Logo"
                    className="w-8 h-8 object-contain"
                    style={{ filter: "brightness(1.2)" }}
                  />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white tracking-tight">CIPHER Security</h1>
                  <p className="text-xs text-gray-400 font-medium">Admin Dashboard</p>
                </div>
              </div>
              
            </div>

            {/* User Management Section */}
            <div className="flex items-center space-x-4">
              {/* User Info Panel */}
              {user && (
                <div className="flex items-center space-x-2 px-2 py-1 bg-white/5 rounded-md border border-white/[0.08]">
                  <div className="flex items-center justify-center w-6 h-6 bg-[#00a86b]/20 rounded-full border border-[#00a86b]/30">
                    <span className="text-xs font-semibold text-[#00a86b] uppercase">
                      {user.username.charAt(0)}
                    </span>
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-white">{user.username}</p>
                    <p className="text-xs text-gray-400 capitalize">{user.role || 'Administrator'}</p>
                  </div>
                </div>
              )}

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200 border border-transparent hover:border-white/[0.08] hover:scale-105 active:scale-95 group"
                title="Sign out of admin dashboard"
              >
                <LogOut className="w-4 h-4 sm:mr-2 transition-transform group-hover:rotate-12" />
                <span className="text-sm font-medium hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Sophisticated Navigation Segments */}
      <nav className="bg-[#0a0a0a]/50 backdrop-blur-sm border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="bg-[#0a0a0a] p-1 rounded-lg border border-white/[0.08] inline-flex overflow-x-auto">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate(tab.path)}
                    className={`relative flex items-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap group ${
                      activeTab === tab.id
                        ? 'bg-[#00a86b]/20 text-[#00a86b] border border-[#00a86b]/30 shadow-[0_0_0_1px_rgba(0,168,107,0.1)]'
                        : 'text-gray-400 hover:text-white hover:bg-white/5 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.08)]'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2 flex-shrink-0 transition-transform group-hover:scale-105" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                    
                    {activeTab === tab.id && (
                      <div className="absolute inset-0 rounded-md bg-[#00a86b]/5 opacity-50 animate-pulse" />
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center space-x-3">
              <div className="hidden lg:flex items-center space-x-2 text-xs text-gray-400">
                <span>Last updated:</span>
                <span className="text-white font-medium">
                  {new Date().toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false 
                  })}
                </span>
              </div>
              
              <div className="hidden lg:block w-px h-4 bg-white/[0.08]"></div>
              
              <button
                onClick={() => window.location.reload()}
                className="flex items-center px-3 py-1.5 text-xs text-gray-400 hover:text-white hover:bg-white/5 rounded-md transition-all duration-200 hover:scale-105 active:scale-95"
                title="Refresh dashboard"
              >
                <svg className="w-3 h-3 transition-transform hover:rotate-180 duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-6">
        {activeTab === 'submissions' && <AdminContactSubmissions />}
        
        {activeTab === 'campaigns' && <AdminBlogCampaign />}
        
        {activeTab === 'cti' && <CTIBriefingManager />}
        
        {activeTab === 'subscribers' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Subscriber Management</h2>
              <p className="text-gray-300">Coming soon - manage newsletter subscribers</p>
            </div>
          </div>
        )}
        
        {activeTab === 'analytics' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Analytics Dashboard</h2>
              <p className="text-gray-300">Coming soon - detailed campaign analytics</p>
            </div>
          </div>
        )}
        
        {activeTab === 'settings' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 text-center">
              <Settings className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">System Settings</h2>
              <p className="text-gray-300">Coming soon - configure email templates and settings</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;